/**
 * visual-timer — a countdown you read with your eyes, not with your reading.
 *
 * ── What this tool is for ───────────────────────────────────────────────────
 * The category was created by the physical Time Timer: a red disk that shrinks.
 * It exists because a digit is useless to the people who need a timer most —
 * a child who cannot yet read a clock, someone whose sense of elapsed time does
 * not report ("time blindness"), a class reading a board from eight metres. So
 * the shape carries the information; digits are an option you can switch off.
 *
 * ── Where we deliberately differ from every competitor ──────────────────────
 *  1. The ending is forewarned and ramps in (see sound.js). Everyone else goes
 *     from silence to full alarm on one sample, and that moment is documented
 *     as the part anxious and neurodivergent children fear.
 *  2. The alarm is booked on the AudioContext clock, so it fires on time in a
 *     background tab where setInterval is throttled to once a minute.
 *  3. Picture-in-Picture: the timer floats over slides / another app. No web
 *     visual timer we found does this, and it is the difference between "I have
 *     a timer" and "I can see my timer while I teach".
 *  4. Sound, screen flash and vibration are three INDEPENDENT channels, so the
 *     tool works in a silent room and for a deaf user without becoming a
 *     different product.
 *  5. Screen wake lock while running — a projected timer that sleeps at 60 s is
 *     not a timer.
 *
 * ── Safety constraint that shapes the code ──────────────────────────────────
 * The flash is capped at well under three flashes per second (WCAG 2.3.1) and
 * turns into a steady tint under prefers-reduced-motion. The version of this
 * tool that shipped before allowed a 200 ms period — 5 Hz, inside the
 * photosensitive-seizure band. That is why the slider range is what it is; do
 * not widen it.
 */

import { drawScene, DIAL_STYLES, STYLE_KEYS, mix } from './render.js';
import { createSound, VOICES } from './sound.js';

const PALETTE = {
    red: [239, 68, 68],
    orange: [249, 115, 22],
    amber: [245, 158, 11],
    green: [34, 197, 94],
    teal: [20, 184, 166],
    blue: [59, 130, 246],
    violet: [139, 92, 246],
    pink: [236, 72, 153],
};
const WARN_RGB = [245, 158, 11];
const DONE_RGB = [34, 197, 94];

const DEFAULTS = {
    style: 'disc',
    color: 'red',
    numbers: true,
    proportional: false,
    gentle: true,
    warnSec: 120,
    voice: 'chime',
    volume: 0.7,
    flash: true,
    flashColor: '#22c55e',
    flashPeriod: 1000,
    vibrate: true,
    wake: true,
    dragSeen: false,      // the dial hint retires once the gesture has been used
};

function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }

/** H:MM:SS above an hour, MM:SS below — a leading "00:" is noise on a wall. */
function fmt(ms) {
    let s = Math.max(0, Math.ceil(ms / 1000));
    const h = Math.floor(s / 3600); s -= h * 3600;
    const m = Math.floor(s / 60); s -= m * 60;
    const pad = (n) => (n < 10 ? '0' + n : '' + n);
    return h > 0 ? h + ':' + pad(m) + ':' + pad(s) : pad(m) + ':' + pad(s);
}

function hexToRgb(hex) {
    const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(String(hex || ''));
    return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : null;
}

export function mount(root, CFG) {
    if (!root || root.__vtMounted) return;
    root.__vtMounted = true;

    const T = (CFG && CFG.i18n) || {};
    const t = (k) => (T[k] === undefined ? k : T[k]);
    const q = (sel) => root.querySelector(sel);
    const qa = (sel) => Array.prototype.slice.call(root.querySelectorAll(sel));

    const el = {
        stage: q('[data-vt-stage]'),
        canvas: q('[data-vt-canvas]'),
        time: q('[data-vt-time]'),
        status: q('[data-vt-status]'),
        stageName: q('[data-vt-stagename]'),
        start: q('[data-vt-start]'),
        pause: q('[data-vt-pause]'),
        reset: q('[data-vt-reset]'),
        fs: q('[data-vt-fs]'),
        pip: q('[data-vt-pip]'),
        share: q('[data-vt-share]'),
        h: q('[data-vt-h]'),
        m: q('[data-vt-m]'),
        s: q('[data-vt-s]'),
        durBlock: q('[data-vt-durblock]'),
        presetBlock: q('[data-vt-presetblock]'),
        addBlock: q('[data-vt-addblock]'),
        stagesList: q('[data-vt-stages]'),
        stagesBlock: q('[data-vt-stagesblock]'),
        addStage: q('[data-vt-addstage]'),
        flash: q('[data-vt-flash]'),
        dragHint: q('[data-vt-draghint]'),
        aria: q('[data-vt-aria]'),
        pipCanvas: q('[data-vt-pipcanvas]'),
        pipVideo: q('[data-vt-pipvideo]'),
        card: q('[data-vt-card]'),
    };
    if (!el.canvas || !el.stage) return;

    const ctx = el.canvas.getContext('2d');
    const sound = createSound();
    const slug = CFG.slug || 'visual-timer';

    const reduceMotion = window.matchMedia
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /* ── settings ─────────────────────────────────────────────────────────── */
    const S = Object.assign({}, DEFAULTS);
    const store = window.__toolSettings;

    function loadSettings() {
        if (!store || typeof store.load !== 'function') return;
        Object.keys(DEFAULTS).forEach(function (k) {
            const v = store.load(slug, k, undefined);
            if (v === undefined || v === null || v === '') return;
            if (typeof DEFAULTS[k] === 'boolean') S[k] = v === true || v === 'true' || v === 1 || v === '1';
            else if (typeof DEFAULTS[k] === 'number') { const n = parseFloat(v); if (!isNaN(n)) S[k] = n; }
            else S[k] = v;
        });
        if (STYLE_KEYS.indexOf(S.style) < 0) S.style = DEFAULTS.style;
        if (VOICES.indexOf(S.voice) < 0) S.voice = DEFAULTS.voice;
        if (!PALETTE[S.color] && !hexToRgb(S.color)) S.color = DEFAULTS.color;
        S.volume = clamp(S.volume, 0, 1);
        S.flashPeriod = clamp(S.flashPeriod, 600, 2000);
        S.warnSec = clamp(S.warnSec, 0, 3600);
    }
    function saveSetting(k) {
        if (store && typeof store.save === 'function') { try { store.save(slug, k, S[k]); } catch (e) {} }
    }
    loadSettings();

    /* ── timer state ──────────────────────────────────────────────────────── */
    const st = {
        mode: 'timer',
        running: false,
        paused: false,
        done: false,
        durationMs: 5 * 60000,      // current stage (or the whole timer)
        elapsedMs: 0,               // within the current stage
        startedAt: 0,
        stages: [],                 // [{name, secs}] — empty means single duration
        stageIdx: 0,
        repeats: 1,                 // how many times the whole set runs
        round: 1,                   // which pass we are on
        warnFired: false,
    };

    let mountedAt = Date.now();
    let raf = 0, poll = 0;

    /* ── colour resolution ────────────────────────────────────────────────── */
    function baseRgb() {
        return PALETTE[S.color] || hexToRgb(S.color) || PALETTE.red;
    }
    function sceneColor(remainMs, phase) {
        if (phase === 'done') return DONE_RGB;
        const base = baseRgb();
        if (!S.warnSec || phase === 'idle') return base;
        const warnMs = S.warnSec * 1000;
        if (remainMs > warnMs) return base;
        // Ramp over the last second BEFORE the threshold rather than snapping:
        // a hard colour switch is itself a startle, which is the thing we are
        // designing away from.
        const k = clamp((warnMs - remainMs) / 1500, 0, 1);
        return mix(base, WARN_RGB, k);
    }

    /* ── canvas sizing ────────────────────────────────────────────────────── */
    let cw = 0, ch = 0;
    function resize() {
        const r = el.stage.getBoundingClientRect();
        const dpr = Math.min(window.devicePixelRatio || 1, 2.5);
        cw = Math.max(80, Math.round(r.width));
        ch = Math.max(80, Math.round(r.height));
        el.canvas.width = Math.round(cw * dpr);
        el.canvas.height = Math.round(ch * dpr);
        el.canvas.style.width = cw + 'px';
        el.canvas.style.height = ch + 'px';
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    if (window.ResizeObserver) {
        new ResizeObserver(function () { resize(); paint(); }).observe(el.stage);
    } else {
        window.addEventListener('resize', function () { resize(); paint(); });
    }

    /* ── the scene ────────────────────────────────────────────────────────── */
    function remaining() {
        if (st.mode === 'stopwatch') return st.elapsedMs;
        return Math.max(0, st.durationMs - st.elapsedMs);
    }
    function phase() {
        if (st.done) return 'done';
        if (!st.running && !st.paused) return 'idle';
        if (st.mode === 'timer' && S.warnSec && remaining() <= S.warnSec * 1000) return 'warn';
        return 'run';
    }
    function fraction() {
        if (st.mode === 'stopwatch') {
            // A stopwatch has no end, so the dial reads the current minute —
            // still a shape, still readable across a room.
            return (st.elapsedMs % 60000) / 60000;
        }
        return st.durationMs > 0 ? remaining() / st.durationMs : 0;
    }
    function sweep() {
        if (!DIAL_STYLES[S.style] || !S.proportional) return 1;
        if (st.mode === 'stopwatch') return 1;
        // Time Timer semantics: the whole face is one hour, so 15 minutes is a
        // quarter of it. This is what makes the dial teach the clock.
        return clamp(st.durationMs / 3600000, 0.008, 1);
    }
    function scene(w, h, dark) {
        const ph = phase();
        return {
            w: w, h: h,
            p: clamp(fraction(), 0, 1),
            sweep: sweep(),
            // A stopwatch has no end, so it can never be "running out": passing
            // its elapsed time here made every stopwatch under two minutes old
            // paint in the warning colour, because remaining() means the
            // opposite thing in that mode.
            color: sceneColor(st.mode === 'timer' ? remaining() : Infinity, ph),
            phase: ph,
            style: S.style,
            // Whole minutes of THIS duration — the `dots` style turns it into
            // one dot per minute so the shape is countable, not just full/empty.
            units: st.mode === 'timer' ? Math.round(st.durationMs / 60000) : 60,
            t: Date.now() - mountedAt,
            motion: !reduceMotion,
            dark: dark,
        };
    }
    function isDark() {
        const th = document.documentElement.getAttribute('data-theme');
        return th !== 'light';
    }

    // The tab title is the timer you can still read with the window behind three
    // others. Restored verbatim on reset so we never leave the page mislabelled.
    const baseTitle = document.title;
    let lastTitle = '';
    function syncTitle(text) {
        const want = text ? text + ' · ' + baseTitle : baseTitle;
        if (want !== lastTitle) { document.title = want; lastTitle = want; }
    }

    function paint() {
        if (!cw) resize();
        drawScene(ctx, scene(cw, ch, isDark()));
        const ph = phase();
        const showMs = st.mode === 'stopwatch' ? st.elapsedMs : remaining();
        const shown = fmt(showMs);
        el.time.textContent = shown;
        el.time.classList.toggle('is-hidden', !S.numbers);
        root.classList.toggle('is-warn', ph === 'warn');
        root.classList.toggle('is-done', ph === 'done');
        root.classList.toggle('is-running', st.running && !st.paused);
        syncTitle(ph === 'done' ? t('time_up') : (st.running || st.paused) ? shown : '');

        // The heads-up is announced ONCE, not on every frame: a live region that
        // repeats is worse than no live region.
        if (ph === 'warn' && !st.warnFired && st.mode === 'timer') {
            st.warnFired = true;
            announce(shown);
        }
    }

    /* ── transport ────────────────────────────────────────────────────────── */
    function readDuration() {
        const h = parseInt(el.h.value, 10) || 0;
        const m = parseInt(el.m.value, 10) || 0;
        const s = parseInt(el.s.value, 10) || 0;
        return clamp(h, 0, 23) * 3600000 + clamp(m, 0, 59) * 60000 + clamp(s, 0, 59) * 1000;
    }
    function writeDuration(ms) {
        const total = Math.round(ms / 1000);
        el.h.value = Math.floor(total / 3600);
        el.m.value = Math.floor((total % 3600) / 60);
        el.s.value = total % 60;
    }

    function stagesTotalSecs() {
        return st.stages.reduce(function (a, x) { return a + x.secs; }, 0) * Math.max(1, st.repeats);
    }

    function scheduleSignals() {
        // Everything audible is booked on the audio clock so a hidden tab still
        // fires on time. Anything visual is redrawn by the loop when we return.
        sound.stop();
        if (st.mode !== 'timer' || !st.running || st.paused) return;
        const remainSec = remaining() / 1000;
        const isLast = !st.stages.length
            || (st.stageIdx >= st.stages.length - 1 && st.round >= Math.max(1, st.repeats));
        if (S.warnSec > 0 && remainSec > S.warnSec && isLast) {
            sound.cue(S.voice, S.volume, remainSec - S.warnSec);
        }
        if (isLast) {
            sound.ring(S.voice, S.volume, S.gentle, remainSec);
        } else {
            sound.cue(S.voice, S.volume, remainSec);
        }
    }

    function start() {
        if (st.running && !st.paused) return;
        sound.prime();
        if (st.paused) {
            st.paused = false;
            st.startedAt = Date.now() - st.elapsedMs;
            scheduleSignals();
            loop(); updateButtons(); announce(t('resumed'));
            return;
        }
        stopAlert();
        st.done = false;
        st.warnFired = false;
        if (st.mode === 'timer') {
            if (st.stages.length) {
                st.stageIdx = 0;
                st.round = 1;
                st.durationMs = st.stages[0].secs * 1000;
            } else {
                st.durationMs = readDuration();
                if (st.durationMs <= 0) { flashInvalid(); return; }
            }
        }
        st.elapsedMs = 0;
        st.startedAt = Date.now();
        st.running = true;
        st.paused = false;
        scheduleSignals();
        acquireWake();
        updateButtons(); updateStageLabel(); loop();
        announce(t('started'));
        try { window.__stage && window.__stage('started'); } catch (e) {}
    }

    function pause() {
        if (!st.running) return;
        if (st.paused) { start(); return; }
        st.paused = true;
        st.elapsedMs = Date.now() - st.startedAt;
        sound.stop();
        releaseWake();
        updateButtons(); paint(); announce(t('paused'));
    }

    function reset() {
        st.running = false; st.paused = false; st.done = false;
        st.elapsedMs = 0; st.stageIdx = 0; st.round = 1; st.warnFired = false;
        if (st.mode === 'timer' && !st.stages.length) st.durationMs = readDuration();
        sound.stop(); stopAlert(); releaseWake();
        updateButtons(); updateStageLabel(); paint();
        setStatus('');
        syncTitle('');
        announce(t('reset_done'));
    }

    /** "+5 minutes" mid-run — the single most-used control in a real classroom. */
    function extend(sec) {
        if (st.mode === 'stopwatch') return;
        if (st.done) {
            st.done = false; stopAlert();
            st.durationMs = sec * 1000; st.elapsedMs = 0;
            st.startedAt = Date.now(); st.running = true; st.paused = false;
            acquireWake();
        } else if (st.running) {
            st.durationMs = Math.max(1000, st.durationMs + sec * 1000);
        } else {
            st.durationMs = clamp(readDuration() + sec * 1000, 0, 86399000);
            writeDuration(st.durationMs);
        }
        scheduleSignals();
        updateButtons(); paint();
        announce(fmt(remaining()));
    }

    function finishStage() {
        if (!st.stages.length) { finish(); return; }
        const last = st.stageIdx >= st.stages.length - 1;
        if (last && st.round >= Math.max(1, st.repeats)) { finish(); return; }
        if (last) { st.round += 1; st.stageIdx = 0; } else { st.stageIdx += 1; }
        st.durationMs = st.stages[st.stageIdx].secs * 1000;
        st.elapsedMs = 0;
        st.startedAt = Date.now();
        st.warnFired = false;
        scheduleSignals();
        updateStageLabel();
    }

    function finish() {
        st.running = false; st.paused = false; st.done = true;
        st.elapsedMs = st.durationMs;
        releaseWake();
        setStatus(t('time_up'));
        fireAlert();
        updateButtons(); paint();
        announce(t('time_up'));
        try {
            window.__aeToolComplete && window.__aeToolComplete({
                mode: st.mode, style: S.style, gentle: S.gentle ? 1 : 0,
                stages: st.stages.length, repeats: st.repeats,
                duration: Math.round(st.durationMs / 1000),
            });
        } catch (e) {}
    }

    function loop() {
        cancelAnimationFrame(raf);
        if (!st.running || st.paused) { paint(); return; }
        st.elapsedMs = Date.now() - st.startedAt;
        if (st.mode === 'timer' && st.elapsedMs >= st.durationMs) {
            st.elapsedMs = st.durationMs;
            paint();
            finishStage();
            if (st.running) raf = requestAnimationFrame(loop);
            return;
        }
        paint();
        if (pipActive) drawPip();
        raf = requestAnimationFrame(loop);
    }

    // rAF stops in a hidden tab; this interval is the belt to that brace. It is
    // throttled too (≈1/min), which is fine: the audible signal is already
    // booked on the audio clock, and the visual state is recomputed from
    // Date.now() the moment we come back.
    poll = setInterval(function () {
        if (!st.running || st.paused) return;
        st.elapsedMs = Date.now() - st.startedAt;
        if (st.mode === 'timer' && st.elapsedMs >= st.durationMs) {
            st.elapsedMs = st.durationMs;
            finishStage();
        }
    }, 500);

    document.addEventListener('visibilitychange', function () {
        if (document.visibilityState !== 'visible') return;
        if (st.running && !st.paused) { loop(); reacquireWake(); }
        else paint();
    });

    /* ── buttons / labels ─────────────────────────────────────────────────── */
    function updateButtons() {
        const active = st.running || st.paused;
        el.start.hidden = st.running && !st.paused;
        el.pause.hidden = !st.running;
        el.pause.textContent = st.paused ? t('resume') : t('pause');
        el.reset.disabled = !active && !st.done && st.elapsedMs === 0;
        qa('[data-vt-lock]').forEach(function (n) {
            n.classList.toggle('is-locked', active);
        });
        el.start.textContent = st.done ? t('again') : (st.paused ? t('resume') : t('start'));
    }
    function setStatus(text) {
        if (el.status) { el.status.textContent = text || ''; el.status.hidden = !text; }
    }
    function updateStageLabel() {
        if (!el.stageName) return;
        if (st.stages.length && (st.running || st.paused)) {
            const s = st.stages[st.stageIdx];
            let label = (s && s.name ? s.name : t('stage') + ' ' + (st.stageIdx + 1))
                + ' · ' + (st.stageIdx + 1) + '/' + st.stages.length;
            if (st.repeats > 1) label += ' · ' + t('round') + ' ' + st.round + '/' + st.repeats;
            el.stageName.textContent = label;
            el.stageName.hidden = false;
        } else {
            el.stageName.hidden = true;
        }
    }
    function announce(msg) { try { window.__announce && window.__announce(msg); } catch (e) {} if (el.aria) el.aria.textContent = msg; }
    function flashInvalid() {
        el.durBlock && el.durBlock.classList.add('is-invalid');
        setTimeout(function () { el.durBlock && el.durBlock.classList.remove('is-invalid'); }, 600);
        announce(t('set_time_first'));
        setStatus(t('set_time_first'));
    }

    /* ── the alert: three independent channels ────────────────────────────── */
    let vibTimer = null, alertTimer = null;

    function fireAlert() {
        if (S.flash && el.flash) {
            el.flash.style.background = S.flashColor;
            el.flash.style.setProperty('--vt-flash-period', clamp(S.flashPeriod, 600, 2000) + 'ms');
            el.flash.classList.add('is-on');
            el.flash.classList.toggle('is-steady', !!reduceMotion);
            el.flash.hidden = false;
        }
        if (S.vibrate && navigator.vibrate) {
            const step = function () { try { navigator.vibrate([260, 140, 260]); } catch (e) {} };
            step();
            vibTimer = setInterval(step, 1600);
        }
        clearTimeout(alertTimer);
        // Long enough to be noticed from across a room, short enough that an
        // unattended tab does not pulse for ever.
        alertTimer = setTimeout(stopAlert, S.gentle ? 20000 : 12000);
    }

    function stopAlert() {
        if (el.flash) { el.flash.classList.remove('is-on', 'is-steady'); el.flash.hidden = true; }
        if (vibTimer) { clearInterval(vibTimer); vibTimer = null; }
        if (navigator.vibrate) { try { navigator.vibrate(0); } catch (e) {} }
        clearTimeout(alertTimer);
        sound.stop();
    }
    if (el.flash) el.flash.addEventListener('click', stopAlert);

    /* ── wake lock ────────────────────────────────────────────────────────────
       A timer projected on a board that dims after 60 s is not a timer. The
       shared primitive re-arms itself across visibility/bfcache, so all we own
       is the hold and telling the truth about it: `state().held` is derived
       from observable state, never from "we got an object back". */
    function acquireWake() {
        // Бронь звука не зависит от галочки «не гасить экран»: это разные
        // вещи, и звук обязан дожить до срабатывания в любом случае.
        acquireAudioHold();
        if (!S.wake) return;
        try {
            window.__wakeLock && window.__wakeLock.hold && window.__wakeLock.hold('visual-timer');
        } catch (e) {}
        setTimeout(reportWake, 1200);
    }
    function releaseWake() {
        try {
            window.__wakeLock && window.__wakeLock.releaseHold && window.__wakeLock.releaseHold('visual-timer');
        } catch (e) {}
        releaseAudioHold();
    }

    /* ── «здесь забронирован звук» ──────────────────────────────────────────
       Сигнал бронируется на часах звуковой карты ИМЕННО ради скрытой вкладки
       (см. ring() в sound.js), и страница это обещает: «срабатывает по
       расписанию даже в скрытой вкладке». Обещание ломал наш же хук экономии
       батареи в timbrica-core.js: он усыплял всякий контекст без играющего
       медиа-элемента, а усыплённый контекст останавливает свои часы.
       Замер того же класса на /school-bell (22.09.2026): скрытие вкладки →
       контексты suspended → звонок отмечен «не прозвучал». Объявляем бронь. */
    let audioHold = null;
    function acquireAudioHold() {
        try {
            const c = sound.ctx && sound.ctx();
            if (!c || audioHold || !window.__audioBackground || !window.__audioBackground.holdScheduled) return;
            audioHold = window.__audioBackground.holdScheduled(c, 'visual-timer');
        } catch (e) {}
    }
    function releaseAudioHold() {
        try { if (audioHold) audioHold.release(); } catch (e) {}
        audioHold = null;
    }
    function reacquireWake() { if (st.running && !st.paused) acquireWake(); }
    function reportWake() {
        const note = q('[data-vt-wakenote]');
        if (!note) return;
        let held = false;
        try { held = !!(window.__wakeLock && window.__wakeLock.state && window.__wakeLock.state().held); } catch (e) {}
        note.hidden = !(S.wake && st.running && !held);
    }

    /* ── drag-to-set on the dial ──────────────────────────────────────────────
       The physical Time Timer is set by turning the disk, and that gesture is
       most of why it feels like an object rather than a form. Pointer only —
       keyboard users have the number fields and the chips, which is why the
       canvas is not focusable. */
    let dragging = false;
    function angleToMinutes(ev) {
        const r = el.canvas.getBoundingClientRect();
        const cx = r.left + r.width / 2, cy = r.top + r.height / 2;
        let a = Math.atan2(ev.clientY - cy, ev.clientX - cx) + Math.PI / 2;
        while (a < 0) a += Math.PI * 2;
        while (a >= Math.PI * 2) a -= Math.PI * 2;
        const frac = a / (Math.PI * 2);
        return Math.max(1, Math.round(frac * 60));
    }
    function dialSettable() {
        return st.mode === 'timer' && !st.running && !st.paused && !st.stages.length
            && !!DIAL_STYLES[S.style];
    }
    el.canvas.addEventListener('pointerdown', function (ev) {
        if (!dialSettable()) return;
        dragging = true;
        el.canvas.setPointerCapture && el.canvas.setPointerCapture(ev.pointerId);
        applyDial(ev);
    });
    el.canvas.addEventListener('pointermove', function (ev) { if (dragging) applyDial(ev); });
    ['pointerup', 'pointercancel'].forEach(function (n) {
        el.canvas.addEventListener(n, function () { dragging = false; });
    });
    function applyDial(ev) {
        if (!S.dragSeen) { S.dragSeen = true; saveSetting('dragSeen'); updateDialAffordance(); }
        const min = angleToMinutes(ev);
        st.durationMs = min * 60000;
        writeDuration(st.durationMs);
        syncPresetChips();
        paint();
        announce(min + ' ' + t('min_short'));
    }
    function updateDialAffordance() {
        const on = dialSettable();
        el.stage.classList.toggle('is-settable', on);
        if (el.dragHint) el.dragHint.hidden = !on || S.dragSeen;
    }

    /* ── inputs ───────────────────────────────────────────────────────────── */
    [el.h, el.m, el.s].forEach(function (n) {
        n.addEventListener('input', function () {
            if (st.running || st.paused) return;
            st.durationMs = readDuration();
            syncPresetChips(); paint();
        });
        n.addEventListener('blur', function () {
            const max = n === el.h ? 23 : 59;
            n.value = clamp(parseInt(n.value, 10) || 0, 0, max);
            st.durationMs = readDuration(); syncPresetChips(); paint();
        });
    });
    qa('[data-vt-step]').forEach(function (b) {
        b.addEventListener('click', function () {
            if (st.running || st.paused) return;
            const target = q('[data-vt-' + b.getAttribute('data-vt-for') + ']');
            const max = b.getAttribute('data-vt-for') === 'h' ? 23 : 59;
            const d = parseInt(b.getAttribute('data-vt-step'), 10);
            target.value = clamp((parseInt(target.value, 10) || 0) + d, 0, max);
            st.durationMs = readDuration(); syncPresetChips(); paint();
        });
    });
    qa('[data-vt-preset]').forEach(function (b) {
        b.addEventListener('click', function () {
            const sec = parseInt(b.getAttribute('data-vt-preset'), 10);
            if (st.running || st.paused) return;
            st.stages = []; renderStages();
            st.durationMs = sec * 1000;
            writeDuration(st.durationMs);
            st.done = false; stopAlert(); setStatus('');
            syncPresetChips(); updateDialAffordance(); paint();
        });
    });
    function syncPresetChips() {
        qa('[data-vt-preset]').forEach(function (b) {
            const sec = parseInt(b.getAttribute('data-vt-preset'), 10);
            b.classList.toggle('is-active', !st.stages.length && sec * 1000 === st.durationMs);
        });
    }
    qa('[data-vt-add]').forEach(function (b) {
        b.addEventListener('click', function () { extend(parseInt(b.getAttribute('data-vt-add'), 10)); });
    });

    el.start.addEventListener('click', start);
    el.pause.addEventListener('click', pause);
    el.reset.addEventListener('click', reset);

    qa('[data-vt-mode]').forEach(function (b) {
        b.addEventListener('click', function () {
            const m = b.getAttribute('data-vt-mode');
            if (m === st.mode) return;
            st.mode = m;
            reset();
            qa('[data-vt-mode]').forEach(function (x) {
                const on = x.getAttribute('data-vt-mode') === m;
                x.classList.toggle('is-active', on);
                x.setAttribute('aria-selected', on ? 'true' : 'false');
            });
            root.classList.toggle('is-stopwatch', m === 'stopwatch');
            updateDialAffordance(); resize(); paint();
        });
    });

    /* ── style + colour pickers ───────────────────────────────────────────── */
    const swatchCtxs = [];
    qa('[data-vt-style]').forEach(function (b) {
        const key = b.getAttribute('data-vt-style');
        const c = b.querySelector('canvas');
        if (c) {
            const cc = c.getContext('2d');
            const dpr = Math.min(window.devicePixelRatio || 1, 2);
            const sw = 52, sh = 52;
            c.width = sw * dpr; c.height = sh * dpr;
            c.style.width = sw + 'px'; c.style.height = sh + 'px';
            cc.setTransform(dpr, 0, 0, dpr, 0, 0);
            swatchCtxs.push({ ctx: cc, key: key, w: sw, h: sh });
        }
        b.addEventListener('click', function () {
            S.style = key; saveSetting('style');
            syncStyle(); updateDialAffordance(); paint();
        });
    });
    function syncStyle() {
        qa('[data-vt-style]').forEach(function (b) {
            const on = b.getAttribute('data-vt-style') === S.style;
            b.classList.toggle('is-active', on);
            b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        const propRow = q('[data-vt-proprow]');
        if (propRow) propRow.hidden = !DIAL_STYLES[S.style];
    }
    // The swatches animate a live 62%-remaining scene so the choice is made by
    // looking, not by reading seven nouns.
    function paintSwatches() {
        const dark = isDark();
        const now = Date.now() - mountedAt;
        const p = 0.62;
        swatchCtxs.forEach(function (s) {
            drawScene(s.ctx, {
                w: s.w, h: s.h, p: p, sweep: 1, color: baseRgb(), units: 24,
                phase: 'run', style: s.key, t: now, motion: !reduceMotion, dark: dark,
            });
        });
    }

    qa('[data-vt-color]').forEach(function (b) {
        const key = b.getAttribute('data-vt-color');
        const c = PALETTE[key];
        if (c) b.style.setProperty('--vt-sw', 'rgb(' + c.join(',') + ')');
        b.addEventListener('click', function () {
            S.color = key; saveSetting('color');
            syncColor(); paintSwatches(); paint();
        });
    });
    const customColor = q('[data-vt-customcolor]');
    if (customColor) {
        customColor.addEventListener('input', function () {
            S.color = customColor.value; saveSetting('color');
            syncColor(); paintSwatches(); paint();
        });
    }
    function syncColor() {
        qa('[data-vt-color]').forEach(function (b) {
            const on = b.getAttribute('data-vt-color') === S.color;
            b.classList.toggle('is-active', on);
            b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
    }

    /* ── settings controls ────────────────────────────────────────────────── */
    qa('[data-vt-toggle]').forEach(function (b) {
        const key = b.getAttribute('data-vt-toggle');
        b.addEventListener('click', function () {
            S[key] = !S[key]; saveSetting(key);
            syncToggles(); syncSignalSummary();
            if (key === 'wake') { S.wake && st.running ? acquireWake() : releaseWake(); }
            paint();
        });
    });
    function syncToggles() {
        qa('[data-vt-toggle]').forEach(function (b) {
            const on = !!S[b.getAttribute('data-vt-toggle')];
            b.classList.toggle('is-on', on);
            b.setAttribute('aria-checked', on ? 'true' : 'false');
        });
    }

    const warnSel = q('[data-vt-warn]');
    if (warnSel) {
        warnSel.addEventListener('change', function () {
            S.warnSec = clamp(parseInt(warnSel.value, 10) || 0, 0, 3600);
            saveSetting('warnSec'); scheduleSignals(); syncSignalSummary(); paint();
        });
    }
    const voiceSel = q('[data-vt-voice]');
    if (voiceSel) {
        voiceSel.addEventListener('change', function () {
            S.voice = voiceSel.value; saveSetting('voice');
            sound.preview(S.voice, S.volume); scheduleSignals(); syncSoundRow(); syncSignalSummary();
        });
    }
    const volRange = q('[data-vt-volume]');
    if (volRange) {
        volRange.addEventListener('input', function () {
            S.volume = clamp(parseFloat(volRange.value), 0, 1); saveSetting('volume');
        });
        volRange.addEventListener('change', function () { sound.preview(S.voice, S.volume); scheduleSignals(); });
    }
    const flashColorIn = q('[data-vt-flashcolor]');
    if (flashColorIn) {
        flashColorIn.addEventListener('input', function () {
            S.flashColor = flashColorIn.value; saveSetting('flashColor');
        });
    }
    const flashSpeed = q('[data-vt-flashspeed]');
    if (flashSpeed) {
        flashSpeed.addEventListener('input', function () {
            S.flashPeriod = clamp(parseInt(flashSpeed.value, 10) || 1000, 600, 2000);
            saveSetting('flashPeriod');
        });
    }
    function syncSoundRow() {
        const off = S.voice === 'none';
        const vr = q('[data-vt-volrow]');
        if (vr) vr.classList.toggle('is-muted', off);
        if (volRange) volRange.disabled = off;
    }
    /** One line under the fold's title describing the CURRENT signal setup.
     *  Without it the gentle ending — the reason this tool exists — is invisible
     *  behind a closed <details> and nobody ever learns it is there. */
    function syncSignalSummary() {
        const out = q('[data-vt-signalsummary]');
        if (!out) return;
        const parts = [];
        // Reuse the picked <option>'s own words ("За 2 минуты") rather than
        // re-formatting the seconds — 120 s printed as "02:00" reads like a
        // second countdown, not like a heads-up.
        const warnText = warnSel && warnSel.selectedOptions && warnSel.selectedOptions[0]
            ? warnSel.selectedOptions[0].textContent.trim() : '';
        parts.push(S.warnSec > 0 ? (warnText || t('sum_warn')) : t('sum_warn_off'));
        parts.push(S.voice === 'none' ? t('sum_silent') : t('voice_' + S.voice));
        if (S.gentle && S.voice !== 'none') parts.push(t('sum_gentle'));
        if (S.flash) parts.push(t('sum_flash'));
        out.textContent = parts.join(' · ');
    }

    function syncSettingsUI() {
        if (warnSel) warnSel.value = String(S.warnSec);
        if (voiceSel) voiceSel.value = S.voice;
        if (volRange) volRange.value = String(S.volume);
        if (flashColorIn) flashColorIn.value = S.flashColor;
        if (flashSpeed) flashSpeed.value = String(S.flashPeriod);
        syncToggles(); syncSoundRow(); syncSignalSummary();
        if (!navigator.vibrate) { const r = q('[data-vt-vibrow]'); if (r) r.hidden = true; }
    }

    /* ── stages (a lesson is rarely one block of time) ────────────────────── */
    function renderStages() {
        if (!el.stagesList) return;
        el.stagesList.innerHTML = '';
        st.stages.forEach(function (s, i) {
            const row = document.createElement('div');
            row.className = 'vt-stage-row';

            const name = document.createElement('input');
            name.type = 'text'; name.className = 'vt-stage-name';
            name.value = s.name; name.maxLength = 40;
            name.placeholder = t('stage') + ' ' + (i + 1);
            name.setAttribute('aria-label', t('stage_name'));
            name.addEventListener('input', function () { s.name = name.value; });

            const mm = document.createElement('input');
            mm.type = 'number'; mm.className = 'vt-stage-num'; mm.min = '0'; mm.max = '180';
            mm.value = Math.floor(s.secs / 60);
            mm.setAttribute('aria-label', t('minutes'));
            const ss = document.createElement('input');
            ss.type = 'number'; ss.className = 'vt-stage-num'; ss.min = '0'; ss.max = '59';
            ss.value = s.secs % 60;
            ss.setAttribute('aria-label', t('seconds'));
            function sync() {
                s.secs = Math.max(1, clamp(parseInt(mm.value, 10) || 0, 0, 180) * 60
                    + clamp(parseInt(ss.value, 10) || 0, 0, 59));
                updateStagesTotal();
            }
            mm.addEventListener('input', sync); ss.addEventListener('input', sync);

            const del = document.createElement('button');
            del.type = 'button'; del.className = 'vt-stage-del';
            del.setAttribute('aria-label', t('remove_stage'));
            del.textContent = '×';
            del.addEventListener('click', function () {
                st.stages.splice(i, 1); renderStages(); afterStagesChange();
            });

            const sep = document.createElement('span');
            sep.className = 'vt-stage-sep'; sep.textContent = ':';

            row.appendChild(name); row.appendChild(mm); row.appendChild(sep);
            row.appendChild(ss); row.appendChild(del);
            el.stagesList.appendChild(row);
        });
        updateStagesTotal();
    }
    function updateStagesTotal() {
        const tot = q('[data-vt-stagestotal]');
        if (tot) {
            tot.textContent = st.stages.length ? fmt(stagesTotalSecs() * 1000) : '';
            tot.hidden = !st.stages.length;
        }
    }
    function afterStagesChange() {
        if (!st.stages.length) { st.durationMs = readDuration(); }
        const rr = q('[data-vt-repeatrow]');
        if (rr) rr.hidden = st.stages.length === 0;
        el.durBlock && el.durBlock.classList.toggle('is-superseded', st.stages.length > 0);
        el.presetBlock && el.presetBlock.classList.toggle('is-superseded', st.stages.length > 0);
        syncPresetChips(); updateDialAffordance(); paint();
    }
    if (el.addStage) {
        el.addStage.addEventListener('click', function () {
            if (st.stages.length >= 12) return;
            st.stages.push({ name: '', secs: st.stages.length ? 300 : Math.max(60, Math.round(readDuration() / 1000)) });
            renderStages(); afterStagesChange();
            const rows = el.stagesList.querySelectorAll('.vt-stage-name');
            if (rows.length) rows[rows.length - 1].focus();
        });
    }
    const repeatIn = q('[data-vt-repeat]');
    if (repeatIn) {
        repeatIn.addEventListener('input', function () {
            st.repeats = clamp(parseInt(repeatIn.value, 10) || 1, 1, 99);
            updateStagesTotal();
        });
        repeatIn.addEventListener('blur', function () { repeatIn.value = st.repeats; });
    }

    const clearStages = q('[data-vt-clearstages]');
    if (clearStages) {
        clearStages.addEventListener('click', function () {
            st.stages = []; renderStages(); afterStagesChange();
        });
    }

    /* ── fullscreen / projector mode ──────────────────────────────────────── */
    // The ROOT goes fullscreen, not the card: the card carries backdrop-filter,
    // which would become the containing block for the position:fixed flash
    // overlay and trap the silent channel inside a 900 px box.
    const fsTarget = root;
    const hasFsApi = !!(fsTarget.requestFullscreen || fsTarget.webkitRequestFullscreen);
    let idleTimer = 0;

    function inFs() {
        return !!(document.fullscreenElement || document.webkitFullscreenElement)
            || root.classList.contains('is-fauxfs');
    }
    function toggleFs() {
        if (inFs()) { exitFs(); return; }
        if (hasFsApi) {
            const p = (fsTarget.requestFullscreen || fsTarget.webkitRequestFullscreen).call(fsTarget);
            if (p && p.catch) p.catch(function () { fauxFs(true); });
        } else {
            fauxFs(true);
        }
        armIdle();
    }
    function exitFs() {
        if (document.fullscreenElement && document.exitFullscreen) document.exitFullscreen().catch(function () {});
        else if (document.webkitFullscreenElement && document.webkitExitFullscreen) document.webkitExitFullscreen();
        fauxFs(false);
    }
    function fauxFs(on) {
        // iOS Safari has no Element.requestFullscreen; a fixed overlay is the
        // only projector mode available there.
        root.classList.toggle('is-fauxfs', on);
        document.body.style.overflow = on ? 'hidden' : '';
        syncFsState();
    }
    function syncFsState() {
        const on = inFs();
        root.classList.toggle('is-fs', on);
        if (el.fs) {
            el.fs.setAttribute('aria-pressed', on ? 'true' : 'false');
            el.fs.title = on ? t('exit_fullscreen') : t('fullscreen');
        }
        resize(); paint();
        if (on) armIdle(); else { root.classList.remove('is-idle'); clearTimeout(idleTimer); }
    }
    function armIdle() {
        clearTimeout(idleTimer);
        root.classList.remove('is-idle');
        idleTimer = setTimeout(function () {
            if (inFs()) root.classList.add('is-idle');
        }, 3500);
    }
    ['pointermove', 'pointerdown', 'keydown'].forEach(function (n) {
        root.addEventListener(n, function () { if (inFs()) armIdle(); });
    });
    ['fullscreenchange', 'webkitfullscreenchange'].forEach(function (n) {
        document.addEventListener(n, syncFsState);
    });
    if (el.fs) el.fs.addEventListener('click', toggleFs);
    const fsExit = q('[data-vt-fsexit]');
    if (fsExit) fsExit.addEventListener('click', exitFs);

    /* ── Picture-in-Picture — the timer that survives you switching apps ──── */
    let pipActive = false;
    const hasPip = 'pictureInPictureEnabled' in document && document.pictureInPictureEnabled
        && el.pipCanvas && el.pipVideo && el.pipCanvas.captureStream;
    if (el.pip) el.pip.hidden = !hasPip;

    function drawPip() {
        if (!el.pipCanvas) return;
        const c = el.pipCanvas.getContext('2d');
        const W = el.pipCanvas.width, H = el.pipCanvas.height;
        c.fillStyle = isDark() ? '#0b1220' : '#f4f6fb';
        c.fillRect(0, 0, W, H);
        // Left: the shape. Right: the digits. Both, because a PiP window is
        // small and glanced at rather than watched.
        c.save();
        c.translate(10, (H - 100) / 2);
        drawScene(c, Object.assign(scene(100, 100, isDark()), { w: 100, h: 100 }));
        c.restore();
        const ph = phase();
        c.fillStyle = ph === 'done' ? 'rgb(34,197,94)' : (isDark() ? '#fff' : '#0f172a');
        c.font = '600 40px system-ui,-apple-system,Segoe UI,Roboto,sans-serif';
        c.textAlign = 'center'; c.textBaseline = 'middle';
        c.fillText(fmt(st.mode === 'stopwatch' ? st.elapsedMs : remaining()), 10 + 100 + (W - 120) / 2, H / 2 - 8);
        c.font = '15px system-ui,-apple-system,Segoe UI,Roboto,sans-serif';
        c.fillStyle = isDark() ? 'rgba(255,255,255,.55)' : 'rgba(15,23,42,.55)';
        c.fillText(
            ph === 'done' ? t('time_up')
                : st.paused ? t('paused_short')
                : (st.stages.length && st.running && st.stages[st.stageIdx] && st.stages[st.stageIdx].name)
                    ? st.stages[st.stageIdx].name
                    : t(st.mode === 'timer' ? 'timer_mode' : 'stopwatch'),
            10 + 100 + (W - 120) / 2, H / 2 + 30
        );
    }

    function togglePip() {
        if (!hasPip) return;
        if (document.pictureInPictureElement) { document.exitPictureInPicture().catch(function () {}); return; }
        drawPip();
        try {
            el.pipVideo.srcObject = el.pipCanvas.captureStream(8);
        } catch (e) { return; }
        el.pipVideo.play().then(function () {
            return el.pipVideo.requestPictureInPicture();
        }).then(function () {
            pipActive = true;
            el.pip.setAttribute('aria-pressed', 'true');
            if (!st.running) drawPip();
        }).catch(function () {
            try { window.__toast && window.__toast(t('pip_failed'), 'warning'); } catch (e) {}
        });
    }
    if (el.pipVideo) {
        el.pipVideo.addEventListener('leavepictureinpicture', function () {
            pipActive = false;
            if (el.pip) el.pip.setAttribute('aria-pressed', 'false');
            try { el.pipVideo.srcObject = null; } catch (e) {}
        });
    }
    if (el.pip) el.pip.addEventListener('click', togglePip);
    // A PiP window on a paused/idle timer must still repaint on setting changes.
    setInterval(function () { if (pipActive && (!st.running || st.paused)) drawPip(); }, 500);

    /* ── shareable setup ──────────────────────────────────────────────────── */
    function buildLink() {
        const u = new URL(window.location.href);
        u.search = '';
        u.searchParams.set('t', Math.round((st.stages.length ? stagesTotalSecs() * 1000 : readDuration()) / 1000));
        u.searchParams.set('st', S.style);
        u.searchParams.set('c', S.color);
        if (S.warnSec !== DEFAULTS.warnSec) u.searchParams.set('w', S.warnSec);
        if (!S.numbers) u.searchParams.set('n', '0');
        if (S.proportional) u.searchParams.set('pr', '1');
        if (st.stages.length) {
            u.searchParams.set('sq', st.stages.map(function (s) {
                return s.secs + '~' + encodeURIComponent(s.name || '');
            }).join('!'));
            if (st.repeats > 1) u.searchParams.set('rp', st.repeats);
        }
        return u.toString();
    }
    if (el.share) {
        el.share.addEventListener('click', function () {
            const link = buildLink();
            const done = function () {
                try { window.__toast && window.__toast(t('link_copied'), 'success'); } catch (e) {}
            };
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(link).then(done).catch(function () { window.prompt(t('share_setup'), link); });
            } else {
                window.prompt(t('share_setup'), link);
            }
        });
    }
    function applyLink() {
        let p;
        try { p = new URL(window.location.href).searchParams; } catch (e) { return; }
        const stl = p.get('st'); if (stl && STYLE_KEYS.indexOf(stl) >= 0) S.style = stl;
        const col = p.get('c'); if (col && (PALETTE[col] || hexToRgb(col))) S.color = col;
        const w = p.get('w'); if (w !== null) S.warnSec = clamp(parseInt(w, 10) || 0, 0, 3600);
        if (p.get('n') === '0') S.numbers = false;
        if (p.get('pr') === '1') S.proportional = true;
        const sq = p.get('sq');
        if (sq) {
            st.stages = sq.split('!').slice(0, 12).map(function (part) {
                const bits = part.split('~');
                return { secs: clamp(parseInt(bits[0], 10) || 60, 1, 10800), name: decodeURIComponent(bits[1] || '').slice(0, 40) };
            });
        }
        const rp = p.get('rp');
        if (rp && st.stages.length) st.repeats = clamp(parseInt(rp, 10) || 1, 1, 99);
        const tt = p.get('t');
        if (tt !== null && !st.stages.length) {
            const sec = clamp(parseInt(tt, 10) || 0, 0, 86399);
            if (sec > 0) st.durationMs = sec * 1000;
        }
    }

    /* ── keyboard ─────────────────────────────────────────────────────────── */
    document.addEventListener('keydown', function (e) {
        const tag = (e.target && e.target.tagName) || '';
        if ((window.__keyOwnedByFocus ? window.__keyOwnedByFocus(e) : /^(INPUT|TEXTAREA|SELECT)$/.test(tag)) || e.metaKey || e.ctrlKey || e.altKey) return;
        if (!root.isConnected) return;
        if (e.code === 'Space') {
            e.preventDefault();
            if (st.done) { stopAlert(); reset(); }
            else if (st.running) pause();
            else start();
        } else if (e.key === 'r' || e.key === 'R' || e.key === 'к' || e.key === 'К') {
            reset();
        } else if (e.key === 'f' || e.key === 'F' || e.key === 'а' || e.key === 'А') {
            toggleFs();
        } else if (e.key === 'Escape' && inFs()) {
            exitFs();
        }
    });

    /* ── boot ─────────────────────────────────────────────────────────────── */
    applyLink();
    writeDuration(st.durationMs);
    if (repeatIn) repeatIn.value = st.repeats;
    renderStages(); afterStagesChange();
    syncStyle(); syncColor(); syncSettingsUI(); syncPresetChips();
    updateButtons(); updateStageLabel(); updateDialAffordance();
    resize(); paint(); paintSwatches();

    // Ambient repaint: candle flicker, bubbles and the swatch previews are alive
    // even before the first start — a dead preview looks broken. Gated on the
    // card being on screen, so a page left open on the FAQ costs nothing.
    let onScreen = true;
    if (window.IntersectionObserver && el.card) {
        new IntersectionObserver(function (entries) {
            onScreen = entries.some(function (e) { return e.isIntersecting; });
        }, { threshold: 0.01 }).observe(el.card);
    }
    setInterval(function () {
        if (reduceMotion || !onScreen) return;
        if (document.visibilityState !== 'visible') return;
        paintSwatches();
        if (!st.running && (S.style === 'candle' || S.style === 'bubbles')) paint();
    }, 140);

    if (window.matchMedia) {
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const onTheme = function () { paint(); paintSwatches(); };
        mq.addEventListener ? mq.addEventListener('change', onTheme) : mq.addListener(onTheme);
    }
    new MutationObserver(function () { paint(); paintSwatches(); })
        .observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });

    try { window.__stage && window.__stage('ready'); } catch (e) {}
}

function boot() {
    const root = document.querySelector('[data-vt-root]');
    if (!root) return;
    let cfg = {};
    const raw = document.getElementById('vt-config');
    if (raw) { try { cfg = JSON.parse(raw.textContent); } catch (e) {} }
    mount(root, cfg);
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
